import { useEffect, useState, useRef } from 'react';
import { getRelativePosition } from 'chart.js/helpers';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ChartOptions,
  ChartEvent,
  ActiveElement,
  Chart,
} from 'chart.js';
import { IHourTransaction } from '../../../interface/transaction';
import { useTranslation } from 'react-i18next';
import { formatter } from '../../../constants/utilityFunctions';

ChartJS.register(CategoryScale, LinearScale, BarElement);
interface Props {
  formattedHourTransactions: IHourTransaction[];
  selectedDate: string | null;
  setSelectedDate: (d: string) => void;
  handleClickedDate: (d: string) => void;
}

const RecentGraph = ({
  formattedHourTransactions,
  selectedDate,
  setSelectedDate,
  handleClickedDate,
}: Props) => {
  const { t } = useTranslation();
  const chartRef = useRef<Chart<'bar'> | null>(null);

  const [subtitleColor, setSubtitleColor] = useState('#3F4150');
  const [primaryColor, setPrimaryColor] = useState('#0090A6');
  const [borderGrey, setBorderGrey] = useState('#E3E3E3');
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);

  useEffect(() => {
    const updateColors = () => {
      const style = getComputedStyle(document.documentElement);
      setSubtitleColor(style.getPropertyValue('--clr-subtitle').trim() || '#3F4150');
      setPrimaryColor(style.getPropertyValue('--clr-general-primary').trim() || '#0090A6');
      setBorderGrey(style.getPropertyValue('--clr-border-grey-100').trim() || '#E3E3E3');
    };

    updateColors();
    const observer = new MutationObserver(() => {
      updateColors();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'data-theme-color', 'class'],
    });

    return () => observer.disconnect();
  }, []);

  const labels = formattedHourTransactions.map((t) => {
    const [date, day] = (t.formatedDate ?? '').split(' ');
    return [date, day];
  });

  const values = formattedHourTransactions.map((t) => t.billableHours);
  const chartDataMaxValue = values.length > 0 ? Math.max(...values) : 0;
  const maxValue = chartDataMaxValue || 40;

  const handleDateSelection = (clickedItem: IHourTransaction | undefined, index: number) => {
    if (!clickedItem) return;
    if (clickedItem.formatedDate === selectedDate) {
      setSelectedDate('');
      handleClickedDate('');
      setClickedIndex(null);
    } else {
      setClickedIndex(index);
      setSelectedDate(clickedItem.formatedDate as string);
      handleClickedDate(clickedItem.transactionDate);
    }
  };

  useEffect(() => {
    if (!selectedDate) {
      setClickedIndex(null);
      return;
    }

    const index = formattedHourTransactions.findIndex((t) => t.formatedDate === selectedDate);

    if (index !== -1) {
      setClickedIndex(index);
    }
  }, [selectedDate, formattedHourTransactions]);

  const data = {
    labels,
    datasets: [
      {
        label: t('transaction:billableHours'),
        data: values,
        backgroundColor: primaryColor,
        borderRadius: 8,
        borderSkipped: false,
        barThickness: 7,
      },
    ],
  };

  const getStepSize = (max: number): number => {
    if (max < 10) return 2.5;
    if (max > 30) return 10;
    return 5;
  };

  const roundedMaxValue = Math.ceil(maxValue / 10) * 10;
  const stepSize = getStepSize(maxValue);

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: roundedMaxValue,
        grid: {
          color: borderGrey,
          drawTicks: false,
        },
        border: {
          display: false,
          dash: [3, 3],
        },
        ticks: {
          stepSize: stepSize,
          color: subtitleColor,
          font: {
            size: 10,
            weight: 500,
          },
          callback: (value) =>
            `${formatter.regionalNumberFormat({ value: Number(value) })}${t('common:h')}`,
        },
      },
      x: {
        grid: {
          display: false,
          drawTicks: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: (ctx) => (ctx.index === clickedIndex ? '#E74C3C' : subtitleColor),
          font: {
            size: 10,
            weight: 500,
          },
          callback: (_value, index) => labels[index],
        },
      },
    },
    onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
      if (!elements.length) return;
      const clicked = elements[0].index;
      handleDateSelection(formattedHourTransactions[clicked], clicked);
    },
  };

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const handleCanvasClick = (event: MouseEvent) => {
      const canvasPosition = getRelativePosition(event, chart);
      const xAxis = chart.scales.x;

      if (canvasPosition.y <= xAxis.top || canvasPosition.y >= xAxis.bottom + 20) return;

      const ticks = xAxis.ticks;
      for (let i = 0; i < ticks.length; i++) {
        const tickX = xAxis.getPixelForTick(i);
        if (Math.abs(canvasPosition.x - tickX) < 20) {
          handleDateSelection(formattedHourTransactions[i], i);
          break;
        }
      }
    };

    const canvas = chart.canvas;
    canvas.addEventListener('click', handleCanvasClick);

    return () => {
      canvas.removeEventListener('click', handleCanvasClick);
    };
  }, [chartRef, formattedHourTransactions, selectedDate, setSelectedDate, handleClickedDate]);

  return (
    <div className="recent-graph-container">
      <Bar ref={chartRef} data={data} options={options} />
    </div>
  );
};
export default RecentGraph;
